import React, { useContext, useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { useWindowSize } from 'react-use'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { sectionHeaderStyle } from '@/components/organisms/dashboard/style'
import { Button } from '@/components/atoms/button'
import { DetailedReserchPanel, DetailedReserchType } from '@/components/pages/sales-manager/receipt-history/detailed-reserch-panel'
import { capitalize } from '@/libs/text'
import * as api from '@/apis/aipass'
import { LoaderContextCreator } from '@/contexts/loader'
import { useHistory, useLocation } from 'react-router-dom'
import { Checkbox } from '@/components/atoms/checkbox'
import { AccommodationPaging } from '@/components/molecules/accommodation-paging'
import { mainColumnStyle, wholeContainerStyle } from '@/constants/layout'
import { SideMenu } from '@/components/organisms/side-menu'
import { Header } from '@/components/organisms/header'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { isStaffGuardCancelledError, isStaffGuardSessionReauthAbortError } from '@/libs/errors'

export const ReceiptHistory: React.FC = () => {
  const windows = useWindowSize()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { errorHandler } = useErrorHandler()
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const customerId = params.get('customerId')
  const checkinId = params.get('checkinId')
  const reservationId = params.get('reservationId')

  const [detailedReserch, setDetailedReserch] = useState<DetailedReserchType>({
    fileName: '',
    searchBeginDate: undefined,
    searchEndDate: undefined,
  })
  const [isDropDownReserchPanelOpen, setIsDropDownReserchPanelOpen] = useState<boolean>(false)
  const [receiptHistoryTotal, setReceiptHistoryTotal] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const [receiptHistory, setReceiptHistory] = useState<
    Array<{ id: number; fileName: string; isEnabled: boolean; createdAt: string; url: string | undefined }>
  >([])

  const onChangeEnabledStatus = async (id: number, changedStatus: boolean) => {
    setReceiptHistory([
      ...receiptHistory.map(h => {
        if (h.id === id) {
          h.isEnabled = changedStatus
        }
        return h
      }),
    ])
    await api.updateReceiptHistory(`${id}`, changedStatus)
  }

  const fetchReceiptHistory = async (page: number, shouldRetryAfterReauth = true) => {
    setIsLoading(true)
    setPage(page)
    try {
      const res = await api.fetchReceiptHistory({ page, customerId, reservationId, checkinId, ...detailedReserch })
      setReceiptHistory([])
      if (!res) {
        setReceiptHistoryTotal(0)
        return
      }
      setReceiptHistory(res.histories)
      setReceiptHistoryTotal(res.total)
    } catch (error) {
      if (isStaffGuardSessionReauthAbortError(error) && shouldRetryAfterReauth) {
        await fetchReceiptHistory(page, false)
        return
      }
      if (isStaffGuardCancelledError(error)) return
      errorHandler(error)
      setReceiptHistoryTotal(0)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchReceiptHistory(page)
  }, [])

  return (
    <div css={wholeContainerStyle}>
      <SideMenu />
      <div css={mainColumnStyle}>
        <Header title={capitalize(t('issue history'))} isBack goBack={history.goBack} />
        <div css={settingMainContainerStyle} style={{ minHeight: windows.height - 180 }}>
          <div css={mainContainerStyle} style={{ minHeight: windows.height - 105 }}>
            <div css={headerContainerStyle}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div css={headerStyle}>{capitalize(t('issue history'))}</div>
                <div>
                  <Button
                    buttonType={4}
                    width={i18n.language === 'en' ? 118 : 90}
                    height={32}
                    fontSize={12}
                    onClick={() => setIsDropDownReserchPanelOpen(!isDropDownReserchPanelOpen)}
                  >
                    {t('Detailed search')}
                  </Button>
                  {isDropDownReserchPanelOpen && (
                    <DetailedReserchPanel
                      onClickReserch={() => {
                        fetchReceiptHistory(1)
                      }}
                      setIsDropDownReserchPanelOpen={() => setIsDropDownReserchPanelOpen(!isDropDownReserchPanelOpen)}
                      detailedReserch={detailedReserch}
                      setDetailedReserch={setDetailedReserch}
                    />
                  )}
                </div>
              </div>
              <AccommodationPaging page={page} setPage={fetchReceiptHistory} total={receiptHistoryTotal} />
            </div>
            <table css={tableStyle}>
              <thead>
                <tr css={tableHeadStyle}>
                  <th css={tableHeadColStyle} scope="col">
                    {capitalize(t('enable'))}
                  </th>
                  <th css={tableHeadColStyle} scope="col">
                    {capitalize(t('created at'))}
                  </th>
                  <th css={tableHeadColStyle} scope="col">
                    {capitalize(t('file name'))}
                  </th>
                  <th css={tableHeadColStyle} scope="col">
                    {capitalize(t('link'))}
                  </th>
                </tr>
              </thead>
              <tbody>
                {receiptHistory?.map(item => (
                  <tr key={item.createdAt}>
                    <td css={tableBodyColStyle}>
                      <Checkbox value={item.isEnabled} onChange={v => onChangeEnabledStatus(item.id, v)} />
                    </td>
                    <td css={tableBodyColStyle}>{dayjs(item.createdAt).format('MM月DD日 HH:mm')}</td>
                    <td css={tableBodyColStyle}>{item.fileName}</td>
                    <td css={tableBodyColLinkStyle}>
                      {item.url ? (
                        <a href={item.url} css={downloadColorStyle} target="_blank" rel="noreferrer">
                          ダウンロード
                        </a>
                      ) : (
                        '作成中'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

const settingMainContainerStyle = css({ padding: 24 })

const mainContainerStyle = css({
  width: '100%',
  borderRadius: 5,
  boxShadow: '0px 0px 6px #0000001a',
  background: '#FFF',
  overflow: 'hidden',
})

const headerStyle = css(sectionHeaderStyle, {
  height: 50,
  marginLeft: 0,
})

const headerContainerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  backgroundColor: '#FFF',
  alignItems: 'center',
  margin: '0 32px',
})

const tableStyle = css({
  width: '100%',
  padding: '0 32px',
})

const tableHeadStyle = css({ backgroundColor: '#F5F5F5' })

const tableHeadColStyle = css({
  textAlign: 'left',
  color: '#676767',
  fontSize: '12px',
  fontWeight: 'bold',
  padding: '8px 32px',
  verticalAlign: 'middle',
})

const tableBodyColStyle = css({
  padding: '16px 32px',
  borderBottom: '1px solid #f2f2f2',
  fontSize: 14,
  verticalAlign: 'middle',
})

const tableBodyColLinkStyle = css(tableBodyColStyle, {
  fontWeight: 'bold',
  fontSize: 12,
})

const downloadColorStyle = css({ color: '#F2A40B' })
